import qs from 'qs'
import { Logger } from '~/plugins/log'
const logger = new Logger()
logger.setContext('Cart')

const getDefaultState = () => ({
  runcode: null,
  quote: {},
  items: [],
  upsells: {},
  coupons: [],
  itemCount: 0,
  formKey: null,
  cartStateFailure: false,
  messages: [],
  messageTimeout: 5 * 1000,
  giftMessages: [],
  skipFetchCartState: false
})

export const state = getDefaultState

export const mutations = {
  SET_CART_STATE (state, { response }) {
    state.quote = response.quote
    state.upsells = response.upsells
    state.items = response.items
    state.itemCount = parseInt((state.quote || {}).items_qty) || (state.items || []).length || 0
    state.runcode = response.run_code
    state.coupons = response.coupons
  },
  SET_CART_STATE_SUCCESS (state, success) {
    state.cartStateFailure = success
  },
  SET_FORM_KEY (state, formKey) {
    state.formKey = formKey
  },
  CLEAR_SESSION (state) {
    state.quote = {}
    state.items = []
    state.itemCount = 0
    state.formKey = null
    state.runcode = null
    logger.debug('CLEAR_SESSION')
  },
  ADD_ERROR_MESSAGE (state, { body, args }) {
    if (typeof body !== 'string') body = 'Something went wrong, please try again.'
    
    state.messages = [{
      type: 'error',
      body,
      args,
      timestamp: (new Date()).valueOf()
    }, ...state.messages]
  },
  ADD_MESSAGE (state, { body, args }) {
    state.messages = [{
      type: 'success',
      body,
      args,
      timestamp: (new Date()).valueOf()
    }, ...state.messages]
  },
  CLEAN_MESSAGES (state) {
    state.messages = state.messages.filter(message => message.timestamp > (new Date()).valueOf() - state.messageTimeout)
  },
  RESET (state) {
    Object.assign(state, getDefaultState())
  },
  SET_GIFT_MESSAGES (state, response) {
    state.giftMessages = response.results
  },
  SET_SKIP_FETCH_CART_STATE (state, skip) {
    state.skipFetchCartState = skip
  }
}

export const getters = {
  itemCount: state => state.itemCount,
  items: state => state.items || [],
  quote: state => state.quote || {},
  quoteId: state => (state.quote || {}).entity_id || null,
  upsells: state => state.upsells.filter(upsell => upsell.type !== 'microfibre'),
  microfibreUpsell: state => state.upsells.find(upsell => upsell.type === 'microfibre') || false,
  coupons: state => state.coupons,
  coupon: state => (state.quote || {}).coupon_code,
  hasFreeShipping: state => (state.coupons || []).some(coupon => coupon.free_shipping === '2'),
  grandTotal: state => parseFloat((state.quote || {}).grand_total),
  subTotal: state => parseFloat((state.quote || {}).subtotal),
  discount: state => parseFloat((state.quote || {}).subtotal) - parseFloat((state.quote || {}).subtotal_with_discount),
  messages: state => (state.messages || []).sort(
    (a, b) => b.timestamp - a.timestamp
  ).slice(0, 1),
  messageTimeout: state => state.messageTimeout,
  onlyDigitalGiftCards: state => (state.items || []).every(item => item.sku.includes('egiftcard-')),
  onlyGiftCards: state => (state.items || []).every(item => item.sku.includes('egiftcard-') || item.sku.includes('pgiftcard-')),
  anyGiftCards: state => (state.items || []).some(item => item.sku.includes('egiftcard-') || item.sku.includes('pgiftcard-')),
  giftMessage: state => itemId => state.giftMessages.find(message => message.item_id === parseInt(itemId)),
  skuFromItemID: state => itemId => ((state.items || []).find(item => item.item_id === itemId) || {}).sku || '',
  skipFetchCartState: state => state.skipFetchCartState
}

export const actions = {
  async GET_CART_STATE ({ commit, state, dispatch, rootGetters, rootState }, quiet = false) {
    if (rootGetters['session/ignore']) {
      logger.debug('GET_CART_STATE ignored for bot users')
      return
    }

    logger.debug(`GET_CART_STATE frontendid=${rootGetters['store/frontendId']}`)

    const storeRunCode = rootGetters['store/storeRunCode']
    if (storeRunCode !== state.runcode) {
      logger.debug(`CART MISMATCH ${storeRunCode} ${state.runcode}`)
      commit('CLEAR_SESSION')
    }

    try {
      const getFunc = quiet ? this.$ecomAPI.quietGet : this.$ecomAPI.get
      const { data } = await getFunc('cart/', { params: rootState.route.query })
      
      if (data.session_expired) {
        await dispatch('HANDLE_CART_EXPIRATION')
      } else {
        commit('SET_CART_STATE', { response: data })
        commit('SET_CART_STATE_SUCCESS', true)
        logger.debug(`GET_CART_STATE succeeded quiet=${quiet}, items=${(data.items || []).length}`)
      }
    } catch (error) {
      commit('SET_CART_STATE_SUCCESS', false)
      logger.debug('GET_CART_STATE failed')
      this.app.$sentry.captureException(error)
    }
  },

  async GET_FORM_KEY ({ commit, state, rootGetters }) {
    if (state.formKey || rootGetters['session/ignore']) return

    try {
      await this.$ecomAPI.quietGet('storefront/key/')
      logger.debug('GET_FORM_KEY succeeded')
    } catch (error) {
      logger.debug('GET_FORM_KEY failed')
      this.$sentry.captureException(error)
    }
  },

  async UPDATE_QTY ({ commit, state, getters, dispatch }, { qty, itemId }) {
    logger.debug(`UPDATE_QTY ${itemId} -> ${qty}`)

    dispatch('session/TRACK', { event: 'UPDATE_CART_QUANTITY', body: qty, sku: getters.skuFromItemID(itemId) }, { root: true })

    try {
      const body = {
        [`cart[${itemId}][qty]`]: qty,
        form_key: state.formKey
      }
  
      const { data } = await this.$ecomAPI.post(
        'cart/update/',
        qs.stringify(body),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      commit('SET_CART_STATE', { response: data })
      commit('ADD_MESSAGE', { body: data.message, args: data.message_params || {} })
      logger.debug(`UPDATE_QTY ${itemId} -> ${qty} succeeded`)
      return data.status === 'SUCCESS'
    } catch (error) {
      logger.debug(`UPDATE_QTY ${itemId} -> ${qty} failed`)
      this.app.$sentry.captureException(error)
      return false
    }
  },

  async REMOVE_ITEM ({ commit, state, dispatch }, { itemId }) {
    logger.debug(`REMOVE_ITEM ${itemId}`)

    dispatch('session/TRACK', { event: 'REMOVE_FROM_CART', sku: getters.skuFromItemID(itemId) }, { root: true })

    try {
      const body = { form_key: state.formKey }
      const { data } = await this.$ecomAPI.post(
        `cart/delete/id/${itemId}/`,
        qs.stringify(body),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      commit('SET_CART_STATE', { response: data })
      commit('ADD_MESSAGE', { body: data.message, args: data.message_params || {} })
      dispatch('TRACK_REMOVE_FROM_CART')
      logger.debug(`REMOVE_ITEM ${itemId} succeeded`)
      return data.status === 'SUCCESS'
    } catch (error) {
      logger.debug(`REMOVE_ITEM ${itemId} failed`)
      this.app.$sentry.captureException(error)
      return false
    }
  },

  async ADD_TO_CART ({ commit, state, dispatch }, [body, sku, retries = 1]) {
    logger.debug('ADD_TO_CART')

    // Check we have a frontendId set
    dispatch('GET_FRONTEND_ID')

    try {
      body.form_key = state.formKey
      const { data } = await this.$ecomAPI.post(
        'cart/add/',
        qs.stringify(body),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      if (data.status === 'SUCCESS') {
        commit('SET_CART_STATE', { response: data })
        commit('ADD_MESSAGE', { body: data.message, args: data.message_params || {} })
        
        dispatch('TRACK_ADD_TO_CART', { sku, data })
  
        logger.debug('ADD_TO_CART succeeded')
      } else {
        this.app.$sentry.captureException(new Error('ADD_TO_CART failed'), { extra: data })

        if (data.session_expired) {
          await dispatch('HANDLE_CART_EXPIRATION')
          if (retries > 0) return await dispatch('ADD_TO_CART', [body, sku, retries - 1])
        }

        commit('ADD_ERROR_MESSAGE', { body: data.message, args: data.message_params || {} })
        return
      }
      return data.status === 'SUCCESS'
    } catch (error) {
      commit('ADD_ERROR_MESSAGE', { body: 'Unable to add to basket. Please try again.' })
      this.app.$sentry.captureException(error, { action: 'ADD_TO_CART' })
      logger.debug('ADD_TO_CART failed')
      window.location.reload()
      return false
    }
  },

  async ADD_BY_URL ({ commit, dispatch }, [url, sku, retries = 1]) {
    logger.debug('ADD_BY_URL')

    dispatch('GET_FRONTEND_ID')

    if (!url) return false

    try {
      url = url.replace(/^\/checkout\/cart/i, 'cart')
      const { data } = await this.$ecomAPI.post(
        url,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      if (data.status === 'SUCCESS') {
        commit('SET_CART_STATE', { response: data })
        commit('ADD_MESSAGE', { body: data.message, args: data.message_params || {} })
  
        dispatch('TRACK_ADD_TO_CART', { sku, data })
  
        logger.debug('ADD_BY_URL succeeded')
      } else {
        this.app.$sentry.captureException(new Error('ADD_BY_URL failed'), { extra: data })
        
        if (data.session_expired) {
          await dispatch('HANDLE_CART_EXPIRATION')
          if (retries > 0) return await dispatch('ADD_BY_URL', [url, sku, retries - 1])
        }

        commit('ADD_ERROR_MESSAGE', { body: data.message, args: data.message_params || {} })
        return
      }
      return data.status === 'SUCCESS'
    } catch (error) {
      commit('ADD_ERROR_MESSAGE', { body: 'Unable to add to basket. Please try again.' })
      this.app.$sentry.captureException(error, { action: 'ADD_BY_URL' })
      logger.debug('ADD_BY_URL failed')
      window.location.reload()
      return false
    }
  },

  async ADD_BY_SKU ({ commit, dispatch }, sku) {
    logger.debug('ADD_BY_SKU')

    dispatch('GET_FRONTEND_ID')

    try {
      const { data } = await this.$ecomAPI.post(
        'cart/addBySKU/',
        qs.stringify({ sku }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      if (data.status === 'SUCCESS') {
        commit('SET_CART_STATE', { response: data })
        commit('ADD_MESSAGE', { body: data.message, args: data.message_params || {} })
        
        dispatch('TRACK_ADD_TO_CART', { sku, data })
  
        logger.debug('ADD_BY_SKU succeeded')
      } else {
        commit('ADD_ERROR_MESSAGE', { body: data.message, args: data.message_params || {} })
        this.app.$sentry.captureException(new Error('ADD_BY_SKU failed'), { extra: data })

        if (data.session_expired) {
          await dispatch('HANDLE_CART_EXPIRATION')
        }
      }
      return data.status === 'SUCCESS'
    } catch (error) {
      commit('ADD_ERROR_MESSAGE', { body: 'Unable to add to basket. Please try again.' })
      this.app.$sentry.captureException(error, { action: 'ADD_BY_SKU' })
      logger.debug('ADD_BY_SKU failed')
      window.location.reload()
      return false
    }
  },

  async HANDLE_CART_EXPIRATION ({ commit, dispatch }) {
    commit('CLEAR_SESSION')
    commit('store/CLEAR_SESSION', null, { root: true })
    await dispatch('GET_CART_STATE')
  },

  async APPLY_COUPON ({ commit, state, dispatch }, { coupon }) {
    logger.debug(`APPLY_COUPON ${coupon}`)
    logger.debug('NOT IMPLEMENTED - check if coupon is store-restricted')

    try {
      const body = {
        coupon_code: coupon,
        form_key: state.formKey
      }
  
      const { data } = await this.$ecomAPI.post(
        'cart/coupon/',
        qs.stringify(body),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )

      if (data.status === 'SUCCESS') {
        commit('SET_CART_STATE', { response: data })
        await dispatch('session/UPDATE', { code: coupon }, { root: true })
        commit('ADD_MESSAGE', { body: data.message, args: data.message_params || {} })
      } else {
        commit('ADD_ERROR_MESSAGE', { body: data.message, args: data.message_params || {} })
      }
      
      logger.debug(`APPLY_COUPON ${coupon} succeeded`)
      return data
    } catch (error) {
      commit('ADD_ERROR_MESSAGE', { body: 'Unable to apply coupon. Please try again.' })
      if (!error.message.includes('not valid')) this.app.$sentry.captureException(error)
      logger.debug(`APPLY_COUPON ${coupon} failed`)
      return { status: 'ERROR' }
    }
  },

  async REMOVE_COUPON ({ commit, state, dispatch }) {
    logger.debug('REMOVE_COUPON')

    try {
      const { data } = await this.$ecomAPI.post(
        'cart/coupon/',
        qs.stringify({ remove: 1, form_key: state.formKey }),
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      )
      if (data.status === 'SUCCESS') {
        commit('SET_CART_STATE', { response: data })
        commit('ADD_MESSAGE', { body: data.message, args: data.message_params || {} })
        
        await dispatch('session/UPDATE', { code: '' }, { root: true })
  
        logger.debug('REMOVE_COUPON succeeded')
      } else {
        commit('ADD_ERROR_MESSAGE', { body: data.message, args: data.message_params || {} })
      }
      return data.status === 'SUCCESS'
    } catch (error) {
      commit('ADD_ERROR_MESSAGE', { body: 'Unable to remove coupon. Please try again.' })
      this.app.$sentry.captureException(error)
      logger.debug('REMOVE_COUPON failed')
      return false
    }
  },

  async CHANGE_STORE ({ state }, [from, to]) {
    logger.debug('CHANGE_STORE')

    try {
      const { data } = await this.$ecomAPI.post(
        'cart/changeStore/',
        qs.stringify({ from, form_key: state.formKey }),
        {
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          storeCode: to
        }
      )
      logger.debug('CHANGE_STORE succeeded')
      logger.debug(JSON.stringify(data))
      return data.status === 'SUCCESS'
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.debug('CHANGE_STORE failed')
      logger.error(error)
      return false
    }
  },

  async GET_FRONTEND_ID ({ dispatch, rootState }) {
    // If we've got a frontendId already then we can bail now
    if (rootState.store.frontendId) return

    // Else get the cart state to force a new frontendId
    await dispatch('GET_CART_STATE')
  },

  async TRACK_ADD_TO_CART ({ dispatch, rootGetters }, { sku, data }) {
    dispatch('session/TRACK', { event: 'ADD_TO_CART', sku }, { root: true })
    
    await dispatch('session/UPDATE', { add_to_cart: true }, { root: true })
    
    this.$track.ecommerce.addToCart([{ sku }])

    this.$ld.track('add-to-cart')

    const _learnq = window._learnq || []
    _learnq.push(['track', 'Added to Cart', data.klaviyo])
  },

  TRACK_REMOVE_FROM_CART ({ rootGetters }, sku) {
    // GTM is called from cart-item.vue
  },

  async GET_GIFT_MESSAGES ({ commit, getters, rootGetters }, quiet = true) {
    const quoteId = getters.quoteId
    if (!quoteId) return

    try {
      const { data } = await (quiet ? this.$publicAPI.quietGet : this.$publicAPI.get)(
        'giftmessages/',
        {
          params: {
            limit: 20,
            f: rootGetters['store/frontendId'],
            q: quoteId
          }
        })
      commit('SET_GIFT_MESSAGES', data)
    } catch (error) {
      this.app.$sentry.captureException(error)
      logger.error(error)
    }
  },

  async SET_GIFT_MESSAGE ({ dispatch, getters, rootGetters }, { itemId, message, name }) {
    const quoteId = getters.quoteId
    if (!quoteId || !itemId) return

    await this.$publicAPI.post(
      'giftmessages/modify/',
      {
        frontend_id: rootGetters['store/frontendId'],
        quote_id: quoteId,
        item_id: itemId,
        message,
        name
      })
    await dispatch('GET_GIFT_MESSAGES', true)
  },

  async CHECK_GIFT_MESSAGES ({ getters, dispatch }) {
    // Ensure all relevant items have a gift message set
    await dispatch('GET_GIFT_MESSAGES')
    return getters.items.filter(
      (item) => {
        if (!['pgiftcard', 'giftwrap'].includes(item.sku.split('-')[0])) return false
        if ((getters.giftMessage(item.item_id) || {}).message === '<!BLANK!>') return false
        if (!(getters.giftMessage(item.item_id) || {}).name) return true
        if (!(getters.giftMessage(item.item_id) || {}).message) return true
        return false
      }
    )
  }
}
